import { useState, useEffect } from 'react'
import { Music, Search, Plus, Play, Loader2, AlertCircle, Clock, Disc } from 'lucide-react'
import { spotifyAPI } from '@/lib/api'
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from '@/components/ui/table'

export default function MusicPage() {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState([])
  const [searching, setSearching] = useState(false)
  const [playlist, setPlaylist] = useState(null)
  const [currentlyPlaying, setCurrentlyPlaying] = useState(null)
  const [addingTrack, setAddingTrack] = useState(null)

  useEffect(() => {
    fetchPlaylist()
    fetchCurrentlyPlaying()

    // Poll for currently playing track every 10 seconds
    const interval = setInterval(() => {
      fetchCurrentlyPlaying()
    }, 10000)

    return () => clearInterval(interval)
  }, [])

  const fetchPlaylist = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await spotifyAPI.getPlaylist()
      setPlaylist(response.data)
    } catch (err) {
      console.error('Failed to fetch playlist:', err)
      if (err.response?.status === 400) {
        setError('Nog geen playlist ingesteld. Vraag een admin om de Spotify-integratie op te zetten.')
      } else {
        setError('Failed to load playlist. Please try again later.')
      }
    } finally {
      setLoading(false)
    }
  }

  const fetchCurrentlyPlaying = async () => {
    try {
      const response = await spotifyAPI.getCurrentlyPlaying()
      if (response.data.playing) {
        setCurrentlyPlaying(response.data.track)
      } else {
        setCurrentlyPlaying(null)
      }
    } catch (err) {
      // Silently fail - not critical
      console.error('Failed to fetch currently playing:', err)
    }
  }

  const handleSearch = async (e) => {
    e.preventDefault()
    if (!searchQuery.trim()) return

    try {
      setSearching(true)
      const response = await spotifyAPI.searchTracks(searchQuery, 20)
      setSearchResults(response.data.tracks || [])
    } catch (err) {
      console.error('Failed to search tracks:', err)
      setError('Failed to search tracks. Please try again.')
    } finally {
      setSearching(false)
    }
  }

  const handleAddTrack = async (trackUri, trackName) => {
    try {
      setAddingTrack(trackUri)
      await spotifyAPI.addTrackToPlaylist(trackUri)
      await fetchPlaylist()
      setSearchResults([])
      setSearchQuery('')
      alert(`"${trackName}" has been added to the playlist!`)
    } catch (err) {
      console.error('Failed to add track:', err)
      alert(err.response?.data?.error || 'Failed to add track. Please try again.')
    } finally {
      setAddingTrack(null)
    }
  }

  const formatDuration = (ms) => {
    const minutes = Math.floor(ms / 60000)
    const seconds = Math.floor((ms % 60000) / 1000)
    return `${minutes}:${seconds.toString().padStart(2, '0')}`
  }

  const formatDate = (dateString) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('nl-NL', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    )
  }

  return (
    <div className="container mx-auto px-4 py-8 space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <Music className="h-8 w-8" />
            Muziek
          </h1>
          <p className="text-muted-foreground mt-2">
            Gedeelde afspeellijst, powered by Spotify
          </p>
        </div>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Fout</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Currently Playing */}
      {currentlyPlaying && (
        <Card className="border-green-500/30 bg-green-500/5">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="relative">
                <img
                  src={currentlyPlaying.albumArt}
                  alt={currentlyPlaying.album}
                  className="w-16 h-16 rounded"
                />
                <div className="absolute inset-0 flex items-center justify-center bg-black/40 rounded">
                  <Play className="h-6 w-6 text-green-500 fill-green-500" />
                </div>
              </div>
              <div className="flex-1">
                <p className="text-xs text-green-500 font-semibold uppercase tracking-wide mb-1">Nu bezig</p>
                <h3 className="font-semibold">{currentlyPlaying.name}</h3>
                <p className="text-sm text-muted-foreground">{currentlyPlaying.artists}</p>
              </div>
              <div className="text-sm text-muted-foreground">
                {formatDuration(currentlyPlaying.progress)} / {formatDuration(currentlyPlaying.duration)}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Search */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Search className="h-5 w-5" />
            Zoek nummers
          </CardTitle>
          <CardDescription>
            Zoek nummers om toe te voegen aan de afspeellijst
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSearch} className="flex gap-2">
            <Input
              placeholder="Zoek naar nummers, artiesten of albums…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              disabled={searching}
            />
            <Button type="submit" disabled={searching || !searchQuery.trim()}>
              {searching ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Searching...
                </>
              ) : (
                <>
                  <Search className="h-4 w-4 mr-2" />
                  Search
                </>
              )}
            </Button>
          </form>

          {/* Search Results */}
          {searchResults.length > 0 && (
            <div className="mt-6 space-y-2">
              <h3 className="font-semibold text-sm text-muted-foreground">Search Results</h3>
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12"></TableHead>
                      <TableHead>Track</TableHead>
                      <TableHead>Artist</TableHead>
                      <TableHead>Album</TableHead>
                      <TableHead className="text-right">Duration</TableHead>
                      <TableHead className="text-right">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {searchResults.map((track) => (
                      <TableRow key={track.id}>
                        <TableCell>
                          {track.albumArt ? (
                            <img
                              src={track.albumArt}
                              alt={track.album}
                              className="w-10 h-10 rounded"
                            />
                          ) : (
                            <div className="w-10 h-10 rounded bg-muted flex items-center justify-center">
                              <Disc className="h-5 w-5 text-muted-foreground" />
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="font-medium">{track.name}</TableCell>
                        <TableCell className="text-muted-foreground">{track.artists}</TableCell>
                        <TableCell className="text-muted-foreground text-sm">{track.album}</TableCell>
                        <TableCell className="text-right text-sm">{formatDuration(track.duration)}</TableCell>
                        <TableCell className="text-right">
                          <Button
                            size="sm"
                            onClick={() => handleAddTrack(track.uri, track.name)}
                            disabled={addingTrack === track.uri}
                          >
                            {addingTrack === track.uri ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <>
                                <Plus className="h-4 w-4 mr-1" />
                                Add
                              </>
                            )}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Current Playlist */}
      {playlist && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Music className="h-5 w-5" />
                  {playlist.name}
                </CardTitle>
                {playlist.description && (
                  <CardDescription className="mt-2">{playlist.description}</CardDescription>
                )}
              </div>
              <Badge variant="outline" className="gap-1">
                <Clock className="h-3 w-3" />
                {playlist.totalTracks} tracks
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            {playlist.tracks && playlist.tracks.length > 0 ? (
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12"></TableHead>
                      <TableHead>Track</TableHead>
                      <TableHead>Artist</TableHead>
                      <TableHead>Album</TableHead>
                      <TableHead className="text-right">Duration</TableHead>
                      <TableHead className="text-right">Added</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {playlist.tracks.map((item, index) => (
                      <TableRow key={item.id || index}>
                        <TableCell>
                          {item.albumArt ? (
                            <img
                              src={item.albumArt}
                              alt={item.album}
                              className="w-10 h-10 rounded"
                            />
                          ) : (
                            <div className="w-10 h-10 rounded bg-muted flex items-center justify-center">
                              <Disc className="h-5 w-5 text-muted-foreground" />
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="font-medium">{item.name}</TableCell>
                        <TableCell className="text-muted-foreground">{item.artists}</TableCell>
                        <TableCell className="text-muted-foreground text-sm">{item.album}</TableCell>
                        <TableCell className="text-right text-sm">{formatDuration(item.duration)}</TableCell>
                        <TableCell className="text-right text-sm text-muted-foreground">
                          {formatDate(item.addedAt)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <div className="text-center py-12 text-muted-foreground">
                <Music className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No tracks in the playlist yet.</p>
                <p className="text-sm mt-2">Be the first to add a song!</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  )
}
